import { apiClient } from "./api";

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Remediation recommendation interface
 */
export interface RemediationRecommendation {
  id: string;
  incidentId: string;
  type: string;
  description: string;
  steps: string[];
  status: string;
  priority: string;
  approver?: string;
  rejector?: string;
  rejectionReason?: string;
  executor?: string;
  executionTimeMinutes?: number;
  feedback?: string;
  success?: boolean;
  [key: string]: any;
}

// ============================================================================
// API Functions
// ============================================================================

export const remediationApi = {
  /**
   * Auto-generate remediation recommendations for an incident
   */
  autoGenerateRecommendations: async (incidentId: string): Promise<RemediationRecommendation[]> => {
    const response = await apiClient.post<RemediationRecommendation[]>(
      `/incidents/${incidentId}/remediation-recommendations`
    );
    return response.data;
  },

  /**
   * Get all remediation recommendations for an incident
   */
  getIncidentRecommendations: async (incidentId: string): Promise<RemediationRecommendation[]> => {
    const response = await apiClient.get<RemediationRecommendation[]>(
      `/incidents/${incidentId}/remediation-recommendations`
    );
    return response.data;
  },

  /**
   * Approve a remediation recommendation
   */
  approveRecommendation: async (
    recommendationId: string,
    approver: string
  ): Promise<RemediationRecommendation> => {
    const response = await apiClient.put<RemediationRecommendation>(
      `/remediation-recommendations/${recommendationId}/approve`,
      null,
      {
        params: { approver },
      }
    );
    return response.data;
  },

  /**
   * Reject a remediation recommendation
   */
  rejectRecommendation: async (
    recommendationId: string,
    rejector: string,
    reason: string
  ): Promise<RemediationRecommendation> => {
    const response = await apiClient.put<RemediationRecommendation>(
      `/remediation-recommendations/${recommendationId}/reject`,
      null,
      {
        params: { rejector, reason },
      }
    );
    return response.data;
  },

  /**
   * Execute a remediation recommendation
   */
  executeRecommendation: async (
    recommendationId: string,
    executor: string
  ): Promise<RemediationRecommendation> => {
    const response = await apiClient.post<RemediationRecommendation>(
      `/remediation-recommendations/${recommendationId}/execute`,
      null,
      {
        params: { executor },
      }
    );
    return response.data;
  },

  /**
   * Complete execution of a remediation recommendation
   */
  completeExecution: async (
    recommendationId: string,
    success: boolean,
    executionTimeMinutes: number,
    feedback: string | null = null
  ): Promise<RemediationRecommendation> => {
    const response = await apiClient.put<RemediationRecommendation>(
      `/remediation-recommendations/${recommendationId}/complete`,
      null,
      {
        params: {
          success,
          execution_time_minutes: executionTimeMinutes,
          feedback,
        },
      }
    );
    return response.data;
  },

  /**
   * Create mock remediation data
   */
  createMockData: async (): Promise<any> => {
    const response = await apiClient.post("/mock-data/remediation");
    return response.data;
  },
};

// ============================================================================
// Constants
// ============================================================================

export const REMEDIATION_TYPES = {
  IMMEDIATE_FIX: "IMMEDIATE_FIX",
  PREVENTIVE_MEASURE: "PREVENTIVE_MEASURE",
  ROLLBACK_PROCEDURE: "ROLLBACK_PROCEDURE",
  MONITORING_ENHANCEMENT: "MONITORING_ENHANCEMENT",
  CONFIGURATION_CHANGE: "CONFIGURATION_CHANGE",
  SCRIPT_EXECUTION: "SCRIPT_EXECUTION",
  DEPLOYMENT_ACTION: "DEPLOYMENT_ACTION",
} as const;

export const REMEDIATION_STATUSES = {
  GENERATED: "GENERATED",
  PENDING_APPROVAL: "PENDING_APPROVAL",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  ROLLED_BACK: "ROLLED_BACK",
} as const;

export const REMEDIATION_PRIORITIES = {
  CRITICAL: "CRITICAL",
  HIGH: "HIGH",
  MEDIUM: "MEDIUM",
  LOW: "LOW",
} as const;

export const STATUS_LABELS: Record<string, string> = {
  [REMEDIATION_STATUSES.GENERATED]: "Generated",
  [REMEDIATION_STATUSES.PENDING_APPROVAL]: "Pending Approval",
  [REMEDIATION_STATUSES.APPROVED]: "Approved",
  [REMEDIATION_STATUSES.REJECTED]: "Rejected",
  [REMEDIATION_STATUSES.IN_PROGRESS]: "In Progress",
  [REMEDIATION_STATUSES.COMPLETED]: "Completed",
  [REMEDIATION_STATUSES.FAILED]: "Failed",
  [REMEDIATION_STATUSES.ROLLED_BACK]: "Rolled Back",
};

export const TYPE_LABELS: Record<string, string> = {
  [REMEDIATION_TYPES.IMMEDIATE_FIX]: "Immediate Fix",
  [REMEDIATION_TYPES.PREVENTIVE_MEASURE]: "Preventive Measure",
  [REMEDIATION_TYPES.ROLLBACK_PROCEDURE]: "Rollback Procedure",
  [REMEDIATION_TYPES.MONITORING_ENHANCEMENT]: "Monitoring Enhancement",
  [REMEDIATION_TYPES.CONFIGURATION_CHANGE]: "Configuration Change",
  [REMEDIATION_TYPES.SCRIPT_EXECUTION]: "Script Execution",
  [REMEDIATION_TYPES.DEPLOYMENT_ACTION]: "Deployment Action",
};

// Type exports for constants
export type RemediationType = (typeof REMEDIATION_TYPES)[keyof typeof REMEDIATION_TYPES];
export type RemediationStatus = (typeof REMEDIATION_STATUSES)[keyof typeof REMEDIATION_STATUSES];
export type RemediationPriority =
  (typeof REMEDIATION_PRIORITIES)[keyof typeof REMEDIATION_PRIORITIES];
